/**
 * Profile data layer for the Settings page — one shared query
 * (`GET /api/v1/me/profile`) plus an optimistic merge-PATCH mutation.
 * Mutations roll back the cache and raise a persistent error toast on
 * failure; success toasts are the caller's job (copy differs per card).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { apiFetch } from '@/api/client'
import type { UpdateProfileRequest, UserProfile } from '@/domain'

/** Extracts a human-readable message from an ApiError-or-anything throw. */
function errorMessage(error: unknown): string {
  if (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  return 'Unknown error'
}

export const PROFILE_QUERY_KEY = ['me', 'profile'] as const

export function useProfile() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: ({ signal }) =>
      apiFetch<UserProfile>({ url: '/api/v1/me/profile', signal }),
    // The app shell's user menu mounts this on every page, not just Settings —
    // without a staleTime the default of 0 refetches the profile on every
    // remount and window focus.
    staleTime: 5 * 60_000,
  })
}

export function useProfilePatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: UpdateProfileRequest) =>
      apiFetch<UserProfile>({
        url: '/api/v1/me/profile',
        method: 'PATCH',
        data: patch,
      }),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: PROFILE_QUERY_KEY })
      const previous = qc.getQueryData<UserProfile>(PROFILE_QUERY_KEY)
      if (previous) {
        qc.setQueryData<UserProfile>(PROFILE_QUERY_KEY, { ...previous, ...patch })
      }
      return { previous }
    },
    onError: (error, _patch, context) => {
      if (context?.previous) {
        qc.setQueryData(PROFILE_QUERY_KEY, context.previous)
      }
      toast.error("Couldn't save your changes", { description: errorMessage(error) })
    },
    onSuccess: (updated) => {
      qc.setQueryData(PROFILE_QUERY_KEY, updated)
    },
  })
}
